import type { ProviderId } from '@/types/settings'

/**
 * Contrato común que implementan todos los proveedores de IA
 * (OpenAI, Anthropic, Ollama) y el proxy. El patrón adapter permite
 * intercambiarlos sin tocar la lógica de la app.
 */

/** Mensaje de chat multi-turno (rol + contenido). */
export interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool'
  content: string
  /**
   * En mensajes `assistant`: tool calls que solicitó el modelo. Solo
   * relevante con function-calling (ver `GenerateRequest.tools`).
   */
  toolCalls?: ToolCall[]
  /**
   * En mensajes `tool`: id del `tool_call` al que responden. Requerido por
   * OpenAI/Anthropic para enlazar el resultado con la llamada original.
   */
  toolCallId?: string
}

/**
 * Definición de una herramienta para function-calling. Formato compatible
 * con OpenAI (`functions`/`tools`) y mapeable al de Anthropic
 * (`input_schema`). Solo OpenAI y Anthropic lo soportan hoy; Ollama lo
 * ignora (ver `providers/ollama.ts`).
 */
export interface ToolDefinition {
  type: 'function'
  function: {
    name: string
    description: string
    /** JSON Schema de los parámetros (formato OpenAI). */
    parameters: Record<string, unknown>
  }
}

/** Llamada a herramienta devuelta por el modelo, ya con args parseados. */
export interface ToolCall {
  /** Id que el modelo asigna; se devuelve en el mensaje `tool` de respuesta. */
  id: string
  name: string
  /** Argumentos parseados del JSON string que emite el modelo. */
  arguments: Record<string, unknown>
}

/**
 * Cómo debe elegir el modelo entre responder o llamar herramientas.
 * - `'auto'`: el modelo decide (default).
 * - `'none'`: prohíbe tools; fuerza respuesta de texto (útil para cerrar
 *   un bucle de agente).
 * - `{ name }`: fuerza una herramienta concreta.
 */
export type ToolChoice = 'auto' | 'none' | { type: 'function'; function: { name: string } }

/** Petición de generación de texto. */
export interface GenerateRequest {
  /** Instrucción del sistema (rol/estilo). */
  system?: string
  /** Texto del usuario (lo que se quiere procesar). */
  user: string
  /**
   * Conversación multi-turno para chat contextual. Si se proporciona,
   * los providers lo usan directamente como historial de mensajes
   * (ignorando `system`/`user` simples). Permite chat sobre el libro.
   */
  messages?: ChatMessage[]
  /** Temperatura 0–2. */
  temperature?: number
  /** Máximo de tokens de salida. */
  maxTokens?: number
  /** Señal para cancelar la generación (AbortController). */
  signal?: AbortSignal
  /**
   * Herramientas disponibles para function-calling. Si se pasan, el modelo
   * puede pedir llamarlas en vez de (o antes de) responder texto. Solo
   * OpenAI y Anthropic lo soportan; Ollama lo ignora. Si se omite, el
   * comportamiento es el de siempre (respuesta de texto).
   */
  tools?: ToolDefinition[]
  /** Cómo elegir entre responder y llamar tools. Default `'auto'`. */
  toolChoice?: ToolChoice
}

/** Métricas opcionales devueltas al terminar. */
export interface UsageStats {
  tokensIn: number | null
  tokensOut: number | null
}

/** Callback que recibe cada fragmento de texto según llega (streaming). */
export type StreamHandler = (delta: string) => void

/** Información de identificación del proveedor para guardar en el resumen. */
export interface ProviderInfo {
  provider: ProviderId | 'proxy'
  model: string
}

/**
 * Resultado de una generación. `toolCalls` solo aparece con function-calling
 * (cuando el modelo pide herramientas en vez de responder texto). Como es
 * opcional, los callers existentes que solo leen `text`/`usage` siguen
 * funcionando sin cambios.
 */
export interface GenerateResult {
  text: string
  usage: UsageStats
  /** Presente si el modelo pidió herramientas en vez de/antes de responder. */
  toolCalls?: ToolCall[]
}

/** Error lanzado por cualquier provider. */
export class AiError extends Error {
  /** Código de estado HTTP cuando aplique. */
  status?: number
  constructor(message: string, status?: number) {
    super(message)
    this.name = 'AiError'
    this.status = status
  }
}

/**
 * Interfaz que deben implementar todos los proveedores de IA.
 * `stream` permite mostrar el texto a medida que se genera; `generate`
 * es un atajo que acumula el resultado completo.
 */
export interface AIProvider {
  /** Identificación del proveedor/modelo activos. */
  readonly info: ProviderInfo

  /**
   * Genera texto llamando al modelo y emite cada delta vía `onToken`.
   * Resuelve con el texto completo, las métricas de uso y, si el modelo
   * pidió herramientas (function-calling), la lista de `toolCalls`.
   */
  stream(req: GenerateRequest, onToken: StreamHandler): Promise<GenerateResult>

  /** Atajo: genera y devuelve el resultado completo de golpe (no streaming). */
  generate(req: GenerateRequest): Promise<GenerateResult>

  /**
   * Comprueba que la configuración funciona (ej. llama a un endpoint
   * barato o lista modelos). Lanza AiError si falla.
   */
  testConnection(): Promise<void>
}
